import React, { useState } from "react";
import { useI18n } from "@/lib/i18n";
import { LogIn, PlayCircle, ListChecks, Keyboard, Eye, Send, BarChart3, Video, Download } from "lucide-react";

const STEPS_EN = [
  { icon: LogIn, title: "Step 1 · Candidate Login", body: "Candidates sign in using credentials emailed by DataSprint." },
  { icon: PlayCircle, title: "Step 2 · Mandatory Training", body: "Candidates must read this step-by-step walkthrough before typing." },
  { icon: ListChecks, title: "Step 3 · Sequential Page Unlock", body: "Only the next available page is exposed via API. Future pages are blocked at the server." },
  { icon: Keyboard, title: "Step 4 · Strict Typing", body: "Encrypted random strings. Character-by-character comparison — no lenient matching." },
  { icon: Eye, title: "Step 5 · Review", body: "Candidate can review before submission. After submit, page is locked permanently." },
  { icon: Send, title: "Step 6 · Submit & Auto-Unlock Next", body: "On submit, accuracy/WPM/mistakes are computed and the next page is unlocked." },
  { icon: BarChart3, title: "Step 7 · Results", body: "Per-page accuracy and a character-level error report are shown to the candidate after submission." },
];

const STEPS_HI = STEPS_EN.map((s) => ({ ...s }));

export default function AdminTraining() {
  const { lang, t } = useI18n();
  const steps = lang === "hi" ? STEPS_HI : STEPS_EN;
  const [active, setActive] = useState(0);
  const [videoLang, setVideoLang] = useState("hi");

  const videoSrc = videoLang === "hi"
    ? "/tutorials/DataSprint_Tutorial_Hindi.mp4"
    : "/tutorials/DataSprint_Tutorial_English.mp4";

  return (
    <div>
      <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">{t("training_videos")}</h1>
      <p className="text-zinc-500 mt-2 max-w-2xl text-sm">The same walkthrough is shown to candidates. Admin can download the source MP4 below.</p>

      {/* Admin-only tutorial video card (with Download button) */}
      <div className="mt-6 rounded-2xl bg-white border border-[var(--line)] overflow-hidden shadow-[0_1px_2px_rgba(10,10,10,0.04)]" data-testid="admin-tutorial-video-card">
        <div className="flex items-center justify-between flex-wrap gap-3 px-5 py-4 border-b border-[var(--line)]">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-[var(--ink)] text-white flex items-center justify-center">
              <Video size={16} />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500">Full Walkthrough Video</div>
              <div className="font-display font-semibold text-[15px]">Step-by-step voiceover walkthrough (Admin view)</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="inline-flex rounded-full border border-[var(--line)] p-0.5 bg-zinc-50">
              <button
                data-testid="admin-tutorial-lang-hi"
                onClick={() => setVideoLang("hi")}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${videoLang === "hi" ? "bg-[var(--ink)] text-white" : "text-zinc-600 hover:text-zinc-900"}`}
              >हिंदी</button>
              <button
                data-testid="admin-tutorial-lang-en"
                onClick={() => setVideoLang("en")}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${videoLang === "en" ? "bg-[var(--ink)] text-white" : "text-zinc-600 hover:text-zinc-900"}`}
              >English</button>
            </div>
            <a
              href={videoSrc}
              download
              data-testid="admin-tutorial-download"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[var(--ink)] text-white text-xs font-semibold hover:bg-[var(--accent)] transition-colors"
            >
              <Download size={12} /> Download
            </a>
          </div>
        </div>
        <div className="bg-black">
          <video
            key={videoSrc}
            data-testid="admin-tutorial-video-player"
            src={videoSrc}
            controls
            playsInline
            preload="metadata"
            className="w-full h-auto max-h-[520px] block mx-auto"
          />
        </div>
      </div>

      <div className="mt-8 grid lg:grid-cols-3 gap-6">
        <ol className="lg:col-span-1 space-y-2">
          {steps.map((s, i) => {
            const Icon = s.icon;
            const cur = i === active;
            return (
              <li key={s.title}>
                <button onClick={() => setActive(i)} className={`w-full text-left flex items-start gap-3 px-4 py-3 rounded-xl border transition-all ${cur ? "border-[var(--ink)] bg-[var(--ink)] text-white" : "border-[var(--line)] bg-white hover:border-zinc-400"}`}>
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${cur ? "bg-white text-[var(--ink)]" : "bg-zinc-100 text-zinc-600"}`}><Icon size={16} /></div>
                  <div className="min-w-0">
                    <div className="text-xs uppercase tracking-widest opacity-70">{i + 1} / {steps.length}</div>
                    <div className="font-display font-semibold text-sm truncate">{s.title.split("·").slice(1).join("·").trim() || s.title}</div>
                  </div>
                </button>
              </li>
            );
          })}
        </ol>
        <div className="lg:col-span-2">
          <div className="card-clean p-6 sm:p-8">
            <div className="text-xs uppercase tracking-widest text-zinc-500">Step {active + 1}</div>
            <h2 className="font-display text-2xl sm:text-3xl font-bold mt-2">{steps[active].title}</h2>
            <p className="mt-4 text-zinc-700 leading-relaxed">{steps[active].body}</p>
            <div className="flex items-center justify-between mt-8">
              <button disabled={active === 0} onClick={() => setActive((a) => Math.max(0, a - 1))} className="btn-ghost disabled:opacity-40">← Previous</button>
              <button disabled={active === steps.length - 1} onClick={() => setActive((a) => Math.min(steps.length - 1, a + 1))} className="btn-primary disabled:opacity-40">Next →</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
